import { useCustomerOrdersV2 } from "@/api/use-customer-orders-v2";
import { ShipmentStatus } from "@/api.types.v2";
import { useTranslation } from "react-i18next";

interface Stat {
  value: string;
  label: string;
  accent?: "warning" | "danger";
}

export function DashboardStats() {
  const { t } = useTranslation("app");
  const { data: ordersResponse } = useCustomerOrdersV2({ page: 1, limit: 200 });
  const orders = ordersResponse?.data ?? [];

  // Two action items: shipments waiting on a customer move.
  //  - Ready to drop off: LABEL_READY without an active pickup booking
  //  - Needs payment:     PENDING_PAYMENT
  let readyToDropOff = 0;
  let needsPayment = 0;
  for (const o of orders) {
    for (const shipment of o.shipments ?? []) {
      if (
        !shipment.cancellationRequestedAt &&
        shipment.status === ShipmentStatus.LABEL_READY &&
        (shipment.pickupBookingId == null ||
          shipment.pickupBooking?.cancelledAt)
      ) {
        readyToDropOff++;
      }
    }
    const s = o.shipments?.[0];
    if (!s || s.cancellationRequestedAt) continue;
    if (s.status === ShipmentStatus.PENDING_PAYMENT) needsPayment++;
  }

  const stats: Stat[] = [
    {
      value: String(readyToDropOff),
      label: t("dashboardStats.readyToDropOff"),
      accent: readyToDropOff > 0 ? "warning" : undefined,
    },
    {
      value: String(needsPayment),
      label: t("dashboardStats.needsPayment"),
      accent: needsPayment > 0 ? "danger" : undefined,
    },
  ];

  const accentClass = (accent?: Stat["accent"]) =>
    accent === "warning"
      ? "text-warning-700"
      : accent === "danger"
        ? "text-danger-700"
        : "text-default-900";

  return (
    <div className="inline-flex items-stretch divide-x divide-default-200 bg-default-50 rounded-large overflow-hidden">
      {stats.map((s) => (
        <div key={s.label} className="px-5 py-2.5 min-w-[140px]">
          <div
            className={`text-xl font-bold tracking-tight leading-tight ${accentClass(s.accent)}`}
          >
            {s.value}
          </div>
          <div className="text-tiny font-semibold uppercase tracking-wider text-default-500 mt-0.5">
            {s.label}
          </div>
        </div>
      ))}
    </div>
  );
}
